import React, { useState, useMemo } from 'react';
import { useSupabaseClient, useUser } from '@supabase/auth-helpers-react';
import Papa from 'papaparse';
import { DownloadIcon, QuestionIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Select,
  Text,
  VStack,
  HStack,
  useToast,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Grid,
  Flex,
  Switch,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure
} from '@chakra-ui/react';
import { validateEmailAddress } from '../../utils/emailAddressValidation';
import { validatePhoneNumber, formatPhoneNumber } from '../../utils/phoneNumberValidation';

const MAX_RECORDS = 200;

const BulkMemberImport = () => {
  const supabase = useSupabaseClient();
  const user = useUser();
  const toast = useToast();
  const { isOpen: isUndoAlertOpen, onOpen: onUndoAlertOpen, onClose: onUndoAlertClose } = useDisclosure();
  const cancelRef = React.useRef();

  const [selectedFile, setSelectedFile] = useState(null);
  const [columnMapping, setColumnMapping] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  });
  const [previewData, setPreviewData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [importSummary, setImportSummary] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [recordDetails, setRecordDetails] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);
  const [undoingImport, setUndoingImport] = useState(false);

  const cleanPhoneNumber = (phone) => {
    return phone ? phone.replace(/\D/g, '') : '';
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      preview: 5,
      complete: (results) => {
        setHeaders(results.meta.fields || []);
        setPreviewData(results.data);
      },
    });
  };

  const handleColumnMappingChange = (field, value) => {
    // Check if the selected value is already used in another field
    const existingField = Object.entries(columnMapping).find(([key, val]) => 
      key !== field && val === value
    );

    if (existingField) {
      toast({
        title: 'Duplicate Column',
        description: `This column is already mapped to ${existingField[0]}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setColumnMapping(prev => ({ ...prev, [field]: value }));
  };

  const validateAndProcessData = async (data) => {
    if (data.length > MAX_RECORDS) {
      throw new Error(`Maximum ${MAX_RECORDS} records allowed for import`);
    }

    const importBatchId = crypto.randomUUID();
    const processedRecords = [];
    const recordDetails = [];
    const duplicates = [];

    // First, get existing records to check for duplicates
    const { data: existingRecords, error: fetchError } = await supabase
      .from('import_contacts')
      .select('first_name, last_name, email, phone_number');

    if (fetchError) {
      console.error('Error fetching existing records:', fetchError);
      throw fetchError;
    }

    data.forEach((record, index) => {
      // Skip empty rows
      if (Object.values(record).every(value => !value)) {
        return;
      }

      const firstName = record[columnMapping.firstName]?.trim();
      const lastName = record[columnMapping.lastName]?.trim();
      const email = record[columnMapping.email]?.trim();
      const phoneNumber = record[columnMapping.phoneNumber]?.trim();
      const fullName = `${firstName} ${lastName}`.trim();
      const rowNumber = index + 2; // +2 because we skip header and 0-based index
      
      // Check for duplicate in current import batch
      const duplicateInBatch = processedRecords.find(
        existing => 
          existing.first_name === firstName &&
          existing.last_name === lastName &&
          existing.email === email &&
          existing.phone_number === phoneNumber
      );

      // Check for duplicate in existing records
      const duplicateExisting = existingRecords.find(
        existing => 
          existing.first_name === firstName &&
          existing.last_name === lastName &&
          existing.email === email &&
          existing.phone_number === phoneNumber
      );

      if (duplicateInBatch || duplicateExisting) {
        const message = duplicateInBatch 
          ? `Duplicate of record on row ${processedRecords.indexOf(duplicateInBatch) + 2} in current import`
          : `Duplicate of existing record in database`;

        duplicates.push({
          fullName,
          rowNumber,
          message,
          details: {
            firstName,
            lastName,
            email: email || '(no email)',
            phoneNumber: phoneNumber || '(no phone)'
          }
        });
        return;
      }

      const missingFields = [];
      if (!firstName) missingFields.push('first name');
      if (!lastName) missingFields.push('last name');
      if (!phoneNumber) missingFields.push('phone number');

      const cleanedPhone = formatPhoneNumber(phoneNumber);
      const hasInvalidPhone = phoneNumber && !validatePhoneNumber(phoneNumber, false);
      const hasInvalidEmail = email ? !validateEmailAddress(email, false) : false;

      const processedRecord = {
        first_name: firstName || '',
        last_name: lastName || '',
        email: email || null,
        phone_number: phoneNumber || '',
        cleaned_phone_number: cleanedPhone || '',
        created_by: user.id,
        import_batch_id: importBatchId,
        has_missing_fields: missingFields.length > 0,
        has_invalid_phone: hasInvalidPhone,
        has_invalid_email: hasInvalidEmail,
        missing_fields: missingFields,
      };

      // Create record detail message
      const errorMessages = [];
      if (missingFields.length > 0) {
        errorMessages.push(`Missing ${missingFields.join(', ')}`);
      }
      if (hasInvalidPhone && phoneNumber) {
        errorMessages.push('Invalid phone number');
      }
      if (hasInvalidEmail) {
        errorMessages.push('Invalid email format');
      }

      recordDetails.push({
        fullName,
        rowNumber,
        status: errorMessages.length > 0 ? `Imported with ${errorMessages.join(', ')}` : 'Successfully imported',
        details: {
          firstName,
          lastName,
          email: email || '(no email)',
          phoneNumber: phoneNumber || '(no phone)'
        }
      });

      processedRecords.push(processedRecord);
    });

    return { processedRecords, recordDetails, duplicates, importBatchId };
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast({
        title: 'Error',
        description: 'Please select a file to import',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const parsePromise = new Promise((resolve) => {
        Papa.parse(selectedFile, {
          header: true,
          skipEmptyLines: true,
          complete: resolve,
        });
      });

      const results = await parsePromise;
      
      // Total records is just the raw data length plus 1 for header
      const totalRecords = results.data.length + 1;

      if (totalRecords > MAX_RECORDS) {
        toast({
          title: 'Error',
          description: `File contains ${totalRecords - 1} records. Maximum allowed is ${MAX_RECORDS - 1} records per import.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      // Filter out empty rows for processing
      const dataRows = results.data.filter(row => {
        const values = Object.values(row);
        return values.some(value => value);
      });
      
      const { processedRecords, recordDetails, duplicates, importBatchId } = await validateAndProcessData(dataRows);

      if (processedRecords.length > 0) {
        const { data: insertedData, error: insertError } = await supabase
          .from('import_contacts')
          .insert(processedRecords)
          .select();

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }
      }

      // Get import summary
      const summary = {
        totalRecords,
        newRecords: processedRecords.length,
        reimportedRecords: 0,
        skippedRecords: duplicates.length,
        errors: recordDetails.filter(r => r.status.includes('with')).length,
        importBatchId: importBatchId,
      };

      setImportSummary(summary);
      setRecordDetails(recordDetails);
      setDuplicates(duplicates);
      setShowSummaryModal(true);
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: 'Import Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const downloadReport = () => {
    const csvContent = [
      ['Row', 'Name', 'Email', 'Phone', 'Status'],
      ...[...recordDetails, ...duplicates]
        .sort((a, b) => a.rowNumber - b.rowNumber)
        .map(record => [
          record.rowNumber,
          `${record.details.firstName} ${record.details.lastName}`,
          record.details.email,
          record.details.phoneNumber,
          record.status || record.message
        ])
    ];

    const csv = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const confirmUndoImport = () => {
    onUndoAlertOpen();
  };

  const handleUndoImport = async () => {
    try {
      setUndoingImport(true);
      onUndoAlertClose();
      
      // Call the undo_import function
      const { data: deletedCount, error } = await supabase
        .rpc('undo_import', { batch_id: importSummary.importBatchId });
        
      if (error) throw error;

      toast({
        title: "Import Undone",
        description: `Successfully removed ${deletedCount} imported records.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Clear the current import data
      setRecordDetails([]);
      setDuplicates([]);
      setImportSummary(null);
      setShowSummaryModal(false);
    } catch (error) {
      console.error('Error undoing import:', error);
      toast({
        title: "Error",
        description: "Failed to undo import: " + error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUndoingImport(false);
    }
  };

  const filteredRecords = useMemo(() => {
    const allRecords = [...recordDetails, ...duplicates].sort((a, b) => a.rowNumber - b.rowNumber);
    if (!showOnlyIssues) return allRecords;
    return allRecords.filter(record => 
      (record.status && record.status.includes('with')) || record.message
    );
  }, [recordDetails, duplicates, showOnlyIssues]);

  return (
    <Box>
      {/* Fixed Header */}
      <Box 
        position="sticky" 
        top="0" 
        bg="white" 
        p={3} 
        zIndex="sticky"
        borderBottomWidth="1px"
        borderColor="gray.200"
        mb={2}
      >
        <Flex justify="space-between" align="center" mb={1}>
          <Text fontSize="2xl" fontWeight="bold">
            Bulk Member Import
          </Text>
          <IconButton
            icon={<QuestionIcon />}
            aria-label="Help"
            variant="ghost"
            onClick={() => {
              toast({
                title: "Help Documentation",
                description: "Documentation for bulk member import will be available soon.",
                status: "info",
                duration: 5000,
                isClosable: true,
              });
            }}
          />
        </Flex>
        <Text color="gray.600" fontSize="sm">
          Upload a CSV file containing member information. Review the data in View Import History. Move the import to the application with Transfer Import data.
        </Text>
      </Box>

      {/* Main Content */}
      <Box px={4}>
        <VStack spacing={4} align="stretch">
          {/* CSV File Section */}
          <Box>
            <Text fontSize="xl" fontWeight="bold" mb={1}>
              CSV File
            </Text>
            <Text fontSize="sm" mb={2} color="gray.600">
             Select a CSV file with member data from your file system. All fields First Name, Last Name, Email, and Phone Number are required. Email is critical for the consent workflow. You can import up to 199 members at once.
            </Text>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{  
                width: '100%',
                padding: '4px'
              }}
            />
          </Box>

          {/* Column Mapping Section */}
          {headers.length > 0 && (
            <Box>
              <Text fontWeight="bold" mb={1}>
                Column Mapping
              </Text>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Map the columns from your CSV file to the required fields below. Select the corresponding column from your file for each field.
              </Text>
              <Grid templateColumns="repeat(4, 1fr)" gap={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" mb={1}>First Name</FormLabel>
                  <Select
                    size="sm"
                    value={columnMapping.firstName || ''}
                    onChange={(e) => handleColumnMappingChange('firstName', e.target.value)}
                  >
                    <option value="">Select column</option>
                    {headers.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" mb={1}>Last Name</FormLabel>
                  <Select
                    size="sm"
                    value={columnMapping.lastName || ''}
                    onChange={(e) => handleColumnMappingChange('lastName', e.target.value)}
                  >
                    <option value="">Select column</option>
                    {headers.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" mb={1}>Email</FormLabel>
                  <Select
                    size="sm"
                    value={columnMapping.email || ''}
                    onChange={(e) => handleColumnMappingChange('email', e.target.value)}
                  >
                    <option value="">Select column</option>
                    {headers.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" mb={1}>Phone Number</FormLabel>
                  <Select
                    size="sm"
                    value={columnMapping.phoneNumber || ''}
                    onChange={(e) => handleColumnMappingChange('phoneNumber', e.target.value)}
                  >
                    <option value="">Select column</option>
                    {headers.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Box>
          )}

          {/* Preview Section */}
          {previewData.length > 0 && (
            <Box>
              <Text fontWeight="bold" mb={2}>
                Preview
              </Text>
              <Box overflowX="auto">
                <Table variant="simple" size="xs">
                  <Thead>
                    <Tr>
                      {Object.keys(previewData[0]).map((header) => (
                        <Th key={header}>{header}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {previewData.map((row, index) => (
                      <Tr key={index}>
                        {Object.values(row).map((value, cellIndex) => (
                          <Td key={cellIndex}>{value}</Td>
                        ))}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          )}

          {/* Import Button */}
          <Button
            colorScheme="blue"
            onClick={handleImport}
            isDisabled={!selectedFile || !Object.values(columnMapping).every(Boolean)}
            size="md"
          >
            Import Members
          </Button>

          {/* Action Items List */}
          {(recordDetails.length > 0 || duplicates.length > 0) && (
            <Alert status="info" mt={4}>
              <AlertIcon />
              <Box width="100%">
                <Flex justify="space-between" align="center" mb={4}>
                  <Text fontWeight="bold">Action Items:</Text>
                  <Flex gap={2} justify="flex-end" minWidth="300px">
                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="show-issues" mb="0" fontSize="sm">
                        Show only records with issues
                      </FormLabel>
                      <Switch
                        id="show-issues"
                        isChecked={showOnlyIssues}
                        onChange={(e) => setShowOnlyIssues(e.target.checked)}
                      />
                    </FormControl>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      leftIcon={<DownloadIcon />}
                      onClick={downloadReport}
                      minWidth="150px"
                    >
                      Download Report
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="red"
                      minWidth="150px"
                      onClick={confirmUndoImport}
                      isLoading={undoingImport}
                      ml={2}
                    >
                      Undo Import
                    </Button>
                  </Flex>
                </Flex>
                <Box 
                  maxHeight="400px" 
                  overflowY="auto" 
                  borderWidth="1px" 
                  borderRadius="md" 
                  p={2}
                  bg="white"
                >
                  {filteredRecords.map((record) => (
                    <Box 
                      key={record.rowNumber} 
                      mb={3}
                      p={3}
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor={record.status?.includes('with') || record.message ? "yellow.200" : "green.200"}
                      bg={record.status?.includes('with') || record.message ? "yellow.50" : "green.50"}
                    >
                      <Text fontWeight="medium">
                        Row {record.rowNumber} - {record.fullName}: {record.status || record.message}
                      </Text>
                      <Text fontSize="sm" color="gray.600" ml={4}>
                        Name: {record.details.firstName} {record.details.lastName}, 
                        Email: {record.details.email}, 
                        Phone: {record.details.phoneNumber}
                      </Text>
                    </Box>
                  ))}
                </Box>
                <Text fontSize="sm" color="gray.600" mt={2}>
                  * This list will remain visible until you start a new import
                </Text>
              </Box>
            </Alert>
          )}

          <Modal isOpen={showSummaryModal} onClose={() => setShowSummaryModal(false)} size="xl">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Import Summary</ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={6}>
                {importSummary && (
                  <VStack align="stretch" spacing={4}>
                    <Grid templateColumns="1fr auto" gap={2}>
                      <Text fontWeight="bold">Total Records Processed Including Header:</Text>
                      <Text textAlign="right">{importSummary.totalRecords}</Text>
                      
                      <Text fontWeight="bold">New Records Imported:</Text>
                      <Text textAlign="right">{importSummary.newRecords}</Text>
                      
                      <Text fontWeight="bold">Records Reimported with Changes:</Text>
                      <Text textAlign="right">{importSummary.reimportedRecords}</Text>
                      
                      <Text fontWeight="bold">Records Skipped (Duplicates):</Text>
                      <Text textAlign="right">{importSummary.skippedRecords}</Text>
                      
                      <Text fontWeight="bold">Messages/Errors:</Text>
                      <Text textAlign="right">{importSummary.errors}</Text>
                    </Grid>

                    {(recordDetails.length > 0 || duplicates.length > 0) && (
                      <Box mt={4}>
                        <Text fontWeight="bold" mb={2}>Details:</Text>
                        <Box 
                          maxHeight="200px" 
                          overflowY="auto" 
                          borderWidth="1px" 
                          borderRadius="md" 
                          p={2}
                        >
                          {[...recordDetails, ...duplicates]
                            .sort((a, b) => a.rowNumber - b.rowNumber)
                            .map((record) => (
                              <Box key={record.rowNumber} mb={2}>
                                <Text>
                                  Row {record.rowNumber} - {record.fullName}: {record.status || record.message}
                                </Text>
                                <Text fontSize="sm" color="gray.600" ml={8}>
                                  Name: {record.details.firstName} {record.details.lastName}, 
                                  Email: {record.details.email}, 
                                  Phone: {record.details.phoneNumber}
                                </Text>
                              </Box>
                            ))}
                        </Box>
                      </Box>
                    )}
                  </VStack>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>
          <AlertDialog
            isOpen={isUndoAlertOpen}
            leastDestructiveRef={cancelRef}
            onClose={onUndoAlertClose}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  Confirm Undo Import
                </AlertDialogHeader>

                <AlertDialogBody>
                  Are you sure you want to undo this import? This action cannot be reversed.
                  All {importSummary?.newRecords} imported records will be permanently deleted.
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={onUndoAlertClose}>
                    Cancel
                  </Button>
                  <Button colorScheme='red' onClick={handleUndoImport} ml={3}>
                    Undo Import
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </VStack>
      </Box>
    </Box>
  );
};

export default BulkMemberImport;
